import {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
} from "react";

import { cn } from "@pontimed/ui";
import { animate, m, useDragControls, useMotionValue, type PanInfo } from "framer-motion";

import { useMediaQuery } from "@/lib/hooks/use-media-query";

const NO_SWIPE_SELECTOR =
  '[data-slot="slider"], [role="slider"], input[type="range"], [data-no-swipe]';

/* Default spring — used for tap-driven changes + snap-back on cancel.
   Tuned from production swipe-debug logs: matches iOS Photos / Mail snap. */
const SPRING = { type: "spring" as const, stiffness: 320, damping: 34 };

/* Velocity-aware spring tuning — gives the strip a different feel for fast
   vs deliberate vs slow swipes:
     • fast (>1000 px/s) → snappier, slightly bouncy (low damping)
     • medium → standard
     • slow / barely-commit → settles softly without overshoot
   Mirrors how iOS native paged carousels behave on flick vs drag. */
function springForVelocity(velocity: number) {
  const absV = Math.abs(velocity);
  if (absV > 1000) return { type: "spring" as const, stiffness: 380, damping: 28 };
  if (absV > 400) return SPRING;
  return { type: "spring" as const, stiffness: 240, damping: 38 };
}

/* Tolerance for "x is already at target" check in the activeTab-sync
   useEffect — spring physics rarely lands on the exact pixel target, so a
   strict equality check fires a redundant animation when the parent re-
   renders with the new activeTab right after a swipe-driven URL update. */
const POSITION_TOLERANCE_PX = 0.5;

// Trace logging for swipe gesture debugging. Default ON in dev. Silence with:
//   window.__pontimedSwipeDebug = false
// in DevTools. Stays silent in prod regardless. Tagged `[swipe]` so it's easy
// to filter in the console.
const swipeLog = (..._args: unknown[]) => {
  // Temporarily silenced — re-enable by uncommenting the block below.
  // if (typeof window === "undefined") return;
  // if (import.meta.env.PROD) return;
  // const flag = (window as { __pontimedSwipeDebug?: boolean }).__pontimedSwipeDebug;
  // if (flag === false) return;
  // console.log("[swipe]", ..._args);
};

export interface SwipeableTabsHandle {
  scrollActiveToTop: () => void;
}

interface SwipeableTabsContentProps<T extends string> {
  activeTab: T;
  tabIds: readonly T[];
  onTabChange: (id: T) => void;
  ref?: Ref<SwipeableTabsHandle>;
  /**
   * Returns the React tree for one panel. Active panel + immediate neighbors
   * mount lazily; once mounted a panel stays mounted (preserves scroll +
   * prevents re-fetch).
   */
  renderPanel: (id: T) => ReactNode;
  className?: string;
  threshold?: number;
  velocityThreshold?: number;
  /**
   * When true, the carousel is replaced by a plain wrapper rendering only the
   * active panel. Use on the OUTER tab when its active panel hosts a nested
   * carousel — prevents the two pan-x gestures from fighting.
   */
  disabled?: boolean;
  /**
   * Called when the user attempts to commit a swipe past the first or last
   * tab. The inner carousel still snaps back; parent is expected to switch
   * its OWN active tab (typical: subtab → outer tab handoff).
   */
  onEdgeOverscroll?: (direction: "next" | "prev") => void;
  /** Px gap between panels visible during drag. Default 16. */
  panelGap?: number;
  /** Optional shared header rendered at top of every panel inside its scroll. */
  panelHeader?: ReactNode;
}

export function SwipeableTabsContent<T extends string>({
  activeTab,
  tabIds,
  onTabChange,
  ref,
  renderPanel,
  className,
  // Defaults tuned from production swipe-debug logs: real-world gestures
  // typically commit at ~40-60px distance and 100-300 px/s velocity. Raising
  // either bar made small/slow but deliberate swipes silently snap back. iOS
  // Photos / Mail / Twitter all commit at similar levels.
  threshold = 30,
  velocityThreshold = 200,
  disabled = false,
  onEdgeOverscroll,
  panelGap = 16,
  panelHeader,
}: SwipeableTabsContentProps<T>) {
  // Enable the carousel on any non-desktop surface so dev-tools mobile mode
  // (which doesn't always flip pointer:coarse), hybrid touch laptops, and
  // narrow viewports all get the gesture. Desktop with mouse stays bypassed
  // — mouse drag for tabs is wrong UX. The 1023px breakpoint matches the
  // AppShell `lg:` switch where SideNav replaces BottomTabBar, so the same
  // line determines "mobile chrome" everywhere.
  const isTouchOrMobileViewport = useMediaQuery("(pointer: coarse), (max-width: 1023px)");
  const useCarousel = isTouchOrMobileViewport && !disabled;

  // One-time mount log so we can confirm carousel branch decision per page.
  // Re-fires only when carousel-mode flips (resize past breakpoint, etc.).
  useEffect(() => {
    swipeLog(
      `mode=${useCarousel ? "carousel" : "bypass"}`,
      `isTouchOrMobileViewport=${isTouchOrMobileViewport}`,
      `disabled=${disabled}`,
      `tabs=${tabIds.length}`,
      `active=${activeTab}`,
      `tabIds=[${tabIds.join(",")}]`,
    );
  }, [useCarousel, isTouchOrMobileViewport, disabled, tabIds.length, activeTab, tabIds]);

  const activeIndex = Math.max(0, tabIds.indexOf(activeTab));

  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const x = useMotionValue(0);
  const dragControls = useDragControls();
  const isDraggingRef = useRef(false);
  const isFirstSyncRef = useRef(true);
  // Tracks the single pointerId currently driving the gesture. `null` =
  // no active gesture. Set on pointerdown, cleared on pointerup / cancel /
  // dragEnd. Multi-touch lock relies on this.
  const activePointerIdRef = useRef<number | null>(null);

  /* Deferred URL updates — Option C from the redesign discussion.
     Race condition: dragEnd used to call animate(x, target) AND
     onTabChange(newId) in the same tick. The onTabChange call propagates
     through the parent → router.push → re-render with new activeTab prop
     → the activeTab-sync useEffect fires a SECOND animate() on the same
     motionValue → framer-motion kills the first animation → strip "freezes"
     for ~250ms then jumps abruptly to the final position. Visible as
     "tabul rămâne agățat și apoi apare în toată pagina".
     Fix: stash the pending tab id in a ref. The animate() controls'
     `.then()` resolves on natural spring completion — at which point x is
     already at target, so we fire onTabChange. The follow-up useEffect
     sees x === target and no-ops. Smooth handoff, single animation.
     Same pattern for onEdgeOverscroll (subtab → outer tab handoff). */
  const pendingTabChangeRef = useRef<T | null>(null);
  const pendingEdgeOverscrollRef = useRef<"next" | "prev" | null>(null);
  // Holds the latest dragEnd-driven animate() controls so we can verify
  // the .then() callback corresponds to the most recent gesture (a tap
  // mid-swipe would replace this and cancel the spring; we don't want
  // the cancelled animation to still fire onTabChange afterwards).
  const dragEndAnimRef = useRef<ReturnType<typeof animate> | null>(null);

  const scrollMemoryRef = useRef<Map<T, number>>(new Map());
  const panelRefsRef = useRef<Map<T, HTMLDivElement>>(new Map());

  const [mounted, setMounted] = useState<Set<T>>(() => {
    const set = new Set<T>([activeTab]);
    if (useCarousel) {
      if (activeIndex - 1 >= 0) set.add(tabIds[activeIndex - 1]);
      if (activeIndex + 1 < tabIds.length) set.add(tabIds[activeIndex + 1]);
    }
    return set;
  });

  useEffect(() => {
    swipeLog(`mounted=[${Array.from(mounted).join(",")}]`);
  }, [mounted]);

  useEffect(() => {
    if (!useCarousel) {
      setMounted((prev) => (prev.has(activeTab) ? prev : new Set(prev).add(activeTab)));
      return;
    }
    setMounted((prev) => {
      const next = new Set(prev);
      next.add(tabIds[activeIndex]);
      if (activeIndex - 1 >= 0) next.add(tabIds[activeIndex - 1]);
      if (activeIndex + 1 < tabIds.length) next.add(tabIds[activeIndex + 1]);
      return next.size === prev.size ? prev : next;
    });
  }, [activeIndex, activeTab, tabIds, useCarousel]);

  const slotWidth = width + panelGap;

  const scrollAncestorRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) {
      scrollAncestorRef.current = null;
      return;
    }
    let p: HTMLElement | null = el.parentElement;
    while (p && p !== document.body) {
      const cs = getComputedStyle(p);
      if (cs.overflowY === "auto" || cs.overflowY === "scroll") {
        scrollAncestorRef.current = p;
        return;
      }
      p = p.parentElement;
    }
    scrollAncestorRef.current = null;
  }, [useCarousel]);

  useLayoutEffect(() => {
    if (!useCarousel) return;
    const el = panelRefsRef.current.get(activeTab);
    if (!el) return;
    const remembered = scrollMemoryRef.current.get(activeTab) ?? 0;
    if (el.scrollTop !== remembered) el.scrollTop = remembered;
  }, [activeTab, useCarousel]);

  useImperativeHandle(
    ref,
    () => ({
      scrollActiveToTop: () => {
        const el = panelRefsRef.current.get(activeTab);
        if (el) {
          scrollMemoryRef.current.set(activeTab, 0);
          el.scrollTo({ top: 0, behavior: "smooth" });
        }
        scrollAncestorRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      },
    }),
    [activeTab],
  );

  useLayoutEffect(() => {
    if (!useCarousel) return;
    const el = containerRef.current;
    if (!el) {
      swipeLog(`measure SKIP — containerRef null`);
      return;
    }
    const update = () => {
      const w = el.offsetWidth;
      swipeLog(`measure — width=${w}, activeIndex=${activeIndex}`);
      setWidth(w);
      if (!isDraggingRef.current) {
        x.set(-activeIndex * (w + panelGap));
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useCarousel, panelGap]);

  useEffect(() => {
    if (!useCarousel || !width) return;
    if (isDraggingRef.current) return;
    const target = -activeIndex * slotWidth;
    if (isFirstSyncRef.current) {
      isFirstSyncRef.current = false;
      x.set(target);
      return;
    }
    // Tolerance check — spring physics never land on the exact pixel target.
    // Strict equality used to fire a redundant animate() right after a
    // swipe-driven URL update (controls.then → onTabChange → re-render →
    // this useEffect → unnecessary spring on top of the just-completed one).
    if (Math.abs(x.get() - target) < POSITION_TOLERANCE_PX) {
      swipeLog(
        `useEffect SKIP — x.get()=${x.get().toFixed(1)} ≈ target=${target} (within ${POSITION_TOLERANCE_PX}px tolerance)`,
      );
      return;
    }
    // External activeTab change (tap on tab, deep link) reaches here. Drop
    // any pending swipe-driven tab change ref — the parent already drove
    // the change via a different code path, our pending callback is stale.
    pendingTabChangeRef.current = null;
    swipeLog(
      `useEffect ANIMATE — x.get()=${x.get().toFixed(1)} → target=${target} (activeIndex=${activeIndex})`,
    );
    const controls = animate(x, target, SPRING);
    return () => controls.stop();
  }, [activeIndex, width, slotWidth, useCarousel, x]);

  if (!useCarousel) {
    return (
      <div className={className}>
        {panelHeader}
        {renderPanel(activeTab)}
      </div>
    );
  }

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null;
    if (target?.closest?.(NO_SWIPE_SELECTOR)) {
      swipeLog(`pointerdown IGNORED — inside no-swipe selector`, target?.tagName);
      return;
    }
    // Multi-touch lock — once a pointer is being tracked (or framer is
    // already dragging), ignore secondary touches. Without this, a second
    // finger fires another `dragControls.start()` which framer-motion drops
    // silently because the first pointer is still owned, and our user-level
    // gesture progress gets lost. Saw this in real swipe-debug logs: two
    // rapid pointerdowns, only one dragStart, drag never recovers.
    if (activePointerIdRef.current !== null || isDraggingRef.current) {
      swipeLog(
        `pointerdown IGNORED — pointer ${activePointerIdRef.current} already active (this id=${e.pointerId})`,
      );
      return;
    }
    swipeLog(
      `pointerdown — type=${e.pointerType}, x=${e.clientX.toFixed(0)}, y=${e.clientY.toFixed(0)}, target=${target?.tagName}`,
    );

    const pointerId = e.pointerId;
    activePointerIdRef.current = pointerId;
    const startX = e.clientX;
    const startY = e.clientY;

    const onMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      const dx = Math.abs(moveEvent.clientX - startX);
      const dy = Math.abs(moveEvent.clientY - startY);
      if (dx >= 6 && dx > dy) {
        swipeLog(`gesture HANDOFF → framer drag, dx=${dx.toFixed(0)}, dy=${dy.toFixed(0)}`);
        cleanup();
        // Pass the CURRENT pointer event (moveEvent) not the original
        // pointerdown. framer-motion's dragControls.start() uses the event's
        // clientX/Y as the gesture origin; passing the stale pointerdown
        // event makes framer think the gesture started at a position the
        // finger has already left, sometimes preventing dragStart from
        // firing at all (observed in production logs: handoff fires but
        // dragStart/dragEnd never follow).
        try {
          dragControls.start(moveEvent);
          swipeLog(`dragControls.start invoked OK`);
        } catch (err) {
          swipeLog(`dragControls.start THREW`, err);
        }
      } else if (dy >= 6) {
        swipeLog(`gesture VERTICAL — releasing, dy=${dy.toFixed(0)}`);
        cleanup();
      }
    };

    const cleanup = () => {
      // Only clear active-pointer slot if WE still own it. dragEnd may have
      // already cleared it (drag completed naturally) and a new gesture
      // could be starting — don't stomp on its state.
      if (activePointerIdRef.current === pointerId) {
        activePointerIdRef.current = null;
      }
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", cleanup);
      window.removeEventListener("pointercancel", cleanup);
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", cleanup);
    window.addEventListener("pointercancel", cleanup);
  };

  const handleDragStart = () => {
    isDraggingRef.current = true;
    swipeLog(`dragStart — width=${width}, activeIndex=${activeIndex}`);
  };

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    isDraggingRef.current = false;
    activePointerIdRef.current = null;
    if (!width) {
      swipeLog(`dragEnd ABORTED — width=0 (carousel not measured yet)`);
      return;
    }
    const { offset, velocity } = info;
    const spring = springForVelocity(velocity.x);
    swipeLog(
      `dragEnd — offset.x=${offset.x.toFixed(0)}, offset.y=${offset.y.toFixed(0)}, velocity.x=${velocity.x.toFixed(0)}, spring={k=${spring.stiffness},d=${spring.damping}}`,
    );

    if (Math.abs(offset.x) < Math.abs(offset.y)) {
      swipeLog(`dragEnd → snap back (mostly vertical)`);
      animate(x, -activeIndex * slotWidth, SPRING);
      return;
    }

    // 12% of panel width — ~48px on a 398px panel. Below this and the gesture
    // is more likely an accidental brush than a tab switch. iOS native uses
    // a similar fraction for paged carousels.
    const distanceCommit = Math.abs(offset.x) > Math.max(threshold, width * 0.12);
    const velocityCommit = Math.abs(velocity.x) > velocityThreshold;

    let direction = 0;
    if (distanceCommit || velocityCommit) {
      direction = offset.x < 0 ? 1 : -1;
    }
    swipeLog(
      `dragEnd → distanceCommit=${distanceCommit}, velocityCommit=${velocityCommit}, direction=${direction}`,
    );

    const wantsNext = direction === 1 && activeIndex === tabIds.length - 1;
    const wantsPrev = direction === -1 && activeIndex === 0;
    if ((wantsNext || wantsPrev) && onEdgeOverscroll) {
      // DEFER the edge handoff until the snap-back spring finishes — same
      // pattern as the regular tab commit below. Otherwise the parent
      // changes its outer tab, which propagates back to us as a new
      // activeTab prop, kicking off a competing animation while our
      // snap-back is still mid-flight.
      const direction2: "next" | "prev" = wantsNext ? "next" : "prev";
      swipeLog(`dragEnd → edge overscroll handoff DEFERRED (${direction2})`);
      pendingEdgeOverscrollRef.current = direction2;
      const ctrl = animate(x, -activeIndex * slotWidth, spring);
      dragEndAnimRef.current = ctrl;
      ctrl.then(() => {
        // Only fire if THIS spring completed naturally (still the latest
        // gesture's animation). A tap mid-spring would replace dragEndAnimRef.
        if (dragEndAnimRef.current !== ctrl) return;
        if (pendingEdgeOverscrollRef.current === direction2) {
          pendingEdgeOverscrollRef.current = null;
          dragEndAnimRef.current = null;
          swipeLog(`spring DONE → calling onEdgeOverscroll(${direction2})`);
          onEdgeOverscroll(direction2);
        }
      });
      return;
    }

    const targetIndex = Math.max(0, Math.min(tabIds.length - 1, activeIndex + direction));

    if (targetIndex !== activeIndex) {
      const newTabId = tabIds[targetIndex];
      swipeLog(`dragEnd → COMMIT DEFERRED to ${newTabId} (index ${targetIndex})`);
      // Defer the URL update (onTabChange) until the spring finishes. The
      // .then() callback resolves on natural completion; if a tap mid-
      // spring replaces dragEndAnimRef, we drop this commit.
      pendingTabChangeRef.current = newTabId;
      const ctrl = animate(x, -targetIndex * slotWidth, spring);
      dragEndAnimRef.current = ctrl;
      ctrl.then(() => {
        if (dragEndAnimRef.current !== ctrl) {
          swipeLog(`spring SUPERSEDED — drop pending commit to ${newTabId}`);
          return;
        }
        if (pendingTabChangeRef.current === newTabId) {
          pendingTabChangeRef.current = null;
          dragEndAnimRef.current = null;
          swipeLog(`spring DONE → calling onTabChange(${newTabId})`);
          onTabChange(newTabId);
        }
      });
    } else {
      swipeLog(`dragEnd → snap back to ${activeTab}`);
      const ctrl = animate(x, -activeIndex * slotWidth, spring);
      dragEndAnimRef.current = ctrl;
      ctrl.then(() => {
        if (dragEndAnimRef.current === ctrl) {
          dragEndAnimRef.current = null;
          swipeLog(`spring DONE — snap back complete`);
        }
      });
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn("relative overflow-hidden", className)}
      style={{
        touchAction: "pan-y",
        height: "calc(100dvh - var(--swipeable-offset, 7rem))",
      }}
    >
      <m.div
        drag="x"
        dragListener={false}
        dragControls={dragControls}
        dragDirectionLock
        dragConstraints={{
          left: width ? -(tabIds.length - 1) * slotWidth : 0,
          right: 0,
        }}
        dragElastic={0.18}
        dragMomentum={false}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        // Fallback signals that fire even when framer's drag lifecycle is
        // misbehaving — onPanStart fires whenever framer detects pan motion
        // on the element regardless of drag-vs-pan. If we see `panStart`
        // logs but no `dragStart`, framer is tracking the gesture but isn't
        // promoting it to a drag.
        onPanStart={(_, info) =>
          swipeLog(
            `panStart — offset.x=${info.offset.x.toFixed(0)}, offset.y=${info.offset.y.toFixed(0)}`,
          )
        }
        onPanEnd={(_, info) =>
          swipeLog(
            `panEnd — offset.x=${info.offset.x.toFixed(0)}, velocity.x=${info.velocity.x.toFixed(0)}`,
          )
        }
        onPointerDown={handlePointerDown}
        style={{
          x,
          width: width ? tabIds.length * width + (tabIds.length - 1) * panelGap : "100%",
          display: "flex",
          gap: panelGap,
          height: "100%",
        }}
      >
        {tabIds.map((id) => (
          <div
            key={id}
            ref={(el) => {
              if (el) panelRefsRef.current.set(id, el);
              else panelRefsRef.current.delete(id);
            }}
            onScroll={(e) => {
              scrollMemoryRef.current.set(id, e.currentTarget.scrollTop);
            }}
            className="overflow-x-hidden overflow-y-auto"
            style={{
              width: width || undefined,
              height: "100%",
              flexShrink: 0,
            }}
          >
            {mounted.has(id) ? (
              <>
                {panelHeader}
                {renderPanel(id)}
              </>
            ) : null}
          </div>
        ))}
      </m.div>
    </div>
  );
}
